'use client'

import { useState, useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { common, createLowlight } from 'lowlight'
import 'highlight.js/styles/atom-one-dark.css' // Deep dark mode theme for code

const lowlight = createLowlight(common)

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

export default function RichTextEditor({ value, onChange, placeholder }: RichTextEditorProps) {
  const [showDropdown, setShowDropdown] = useState(false)
  const [suggestions, setSuggestions] = useState<any[]>([])
  const [cursorPos, setCursorPos] = useState(0)
  const [matchIndex, setMatchIndex] = useState(-1)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // Disable default code block to use the lowlight extension
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Write your content here...',
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        // Tailwind prose classes ensure the editor content matches your site's typography
        class: 'prose prose-invert max-w-none min-h-[120px] focus:outline-none p-4 text-sm text-slate-200',
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())

      // --- Custom Mention Detection Logic ---
      const { state } = editor
      const { selection } = state
      const { empty, $anchor } = selection

      if (!empty) return

      const currentPos = $anchor.pos
      // Extract the text content of the current block up to the cursor
      const textBefore = $anchor.parent.textBetween(0,$anchor.parentOffset, undefined, '\ufffc')
      const match = textBefore.match(/@([a-zA-Z0-9_]*)$/)

      if (match) {
        const searchTerm = match[1]
        setCursorPos(currentPos)
        // Calculate the absolute document position where the '@' symbol started
        setMatchIndex(currentPos - match[0].length)
        setShowDropdown(true)
        fetchSuggestions(searchTerm)
      } else {
        setShowDropdown(false)
        setSuggestions([])
      }
    },
  })

  // Sync editor content when value changes externally (like quoting or clearing)[span_0](start_span)[span_0](end_span)
  useEffect(() => {
    if (!editor) return

    const currentHtml = editor.getHTML()
    // If the parent cleared it[span_1](start_span)[span_1](end_span)
    if (value === '' && currentHtml !== '<p></p>') {
      editor.commands.setContent('')
    } 
    // If an external source (like a quote event) updated the value string
    else if (value && value !== currentHtml) {
      editor.commands.setContent(value)
      // Move cursor to the end of the newly injected content
      editor.commands.setTextSelection(editor.state.doc.content.size)
    }
  }, [value, editor])

  // Fetch matching users from our API
  const fetchSuggestions = async (searchTerm: string) => {
    try {
      const res = await fetch(`/api/users/search?q=${encodeURIComponent(searchTerm)}`)
      if (res.ok) {
        const data = await res.json()
        setSuggestions(data.users || [])
      }
    } catch (error) {
      console.error('Error fetching user suggestions:', error)
    }
  }

  // Insert selected username and delete the typed query
  const selectUser = (userName: string) => {
    if (!editor) return

    editor
      .chain()
      .focus()
      .deleteRange({ from: matchIndex, to: cursorPos })
      .insertContent(`@${userName} `)
      .run()

    setShowDropdown(false)
    setSuggestions([])
  }

  if (!editor) {
    return null
  }

  return (
    <div className="relative w-full">
      <div className="bg-slate-950 rounded-lg overflow-hidden border border-slate-800 focus-within:border-indigo-500 transition-colors">
        
        {/* Custom Tailwind Toolbar */}
        <div className="flex items-center gap-1 p-2 border-b border-slate-800 bg-slate-900/50 flex-wrap">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-1.5 rounded hover:bg-slate-800 text-slate-400 transition-colors ${editor.isActive('bold') ? 'bg-slate-800 text-indigo-400' : ''}`}
          >
            <b className="font-serif">B</b>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-1.5 rounded hover:bg-slate-800 text-slate-400 transition-colors ${editor.isActive('italic') ? 'bg-slate-800 text-indigo-400' : ''}`}
          >
            <i className="font-serif">I</i>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`p-1.5 rounded hover:bg-slate-800 text-slate-400 transition-colors ${editor.isActive('strike') ? 'bg-slate-800 text-indigo-400' : ''}`}
          >
            <s className="font-serif">S</s>
          </button>
          
          <div className="w-px h-4 bg-slate-700 mx-1" />
          
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`p-1.5 rounded hover:bg-slate-800 text-slate-400 text-xs font-bold transition-colors ${editor.isActive('heading', { level: 2 }) ? 'bg-slate-800 text-indigo-400' : ''}`}
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={`p-1.5 rounded hover:bg-slate-800 text-slate-400 text-xs transition-colors ${editor.isActive('blockquote') ? 'bg-slate-800 text-indigo-400' : ''}`}
          >
            " "
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={`p-1.5 rounded hover:bg-slate-800 text-slate-400 text-xs font-mono transition-colors ${editor.isActive('codeBlock') ? 'bg-slate-800 text-indigo-400' : ''}`}
          >
            &lt;/&gt;
          </button>
        </div>

        <EditorContent editor={editor} />
      </div>

      {/* Autocomplete Suggestion Popup */}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute left-4 bottom-full mb-2 w-64 bg-slate-900 border border-slate-800 rounded-xl shadow-2xl overflow-hidden z-50">
          <div className="p-2 text-[10px] uppercase font-bold tracking-wider text-slate-500 border-b border-slate-800 bg-slate-950/50">
            Suggested Users
          </div>
          <div className="flex flex-col">
            {suggestions.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => selectUser(user.name)}
                className="flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-800/80 transition-colors w-full"
              >
                <div className="w-7 h-7 rounded-lg bg-indigo-600/30 text-indigo-400 flex items-center justify-center font-bold text-xs">
                  {user.name?.[0]?.toUpperCase() || 'U'}
                </div>
                <span className="text-sm font-medium text-slate-200">@{user.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
